import { TaskState, TransmissionTaskType } from '@core/transmission/ITask'
import { TaskPool } from '@core/transmission/TaskPool'
import type { AbsTask } from '@core/transmission/AbsTask'
import { INSTANCE as DeviceManager } from '@core/devices/DeviceManager'
import { debounce, throttle } from 'lodash-es'

const DEFAULT_MAX_PARALLEL = 10 //同时执行的任务数量

const TRANSMISSION_FILE_SIZE_MAXIMUM_LIMIT = 1024 * 1024 * 1024

const TASK_START_DELAY = 1000
const TASK_FROM_QUEUE_START_DELAY = 1500

interface SchedulerChangeListener {
  onAdded(task: AbsTask): void
  onUpdated(task: AbsTask, needShow?: boolean): void
  onSuccess(task: AbsTask): void
  onExceedMaximumFileSize(): void
}

export class TaskScheduler<T extends AbsTask> {
  static sequenceId = 1
  #taskPool: TaskPool<T>
  #maxParallelNum: number
  #schedulerChangeListener: SchedulerChangeListener | undefined
  #deviceListUpdatedListener = {
    onInitialized: () => {
      // do nothing
    },
    onDeviceListUpdated: async () => {
      // resume  failed task
      this.#resumeFailedTask()
    },
    onCurrentSelectedDeviceChanged: () => {
      // do nothing
    },
  }

  constructor(maxParallelNum: number = DEFAULT_MAX_PARALLEL, taskPool?: TaskPool<T>) {
    this.#taskPool = taskPool ?? new TaskPool()
    this.#maxParallelNum = maxParallelNum
    DeviceManager.addChangeListener(this.#deviceListUpdatedListener)
  }

  static getSequenceId(): number {
    const result = TaskScheduler.sequenceId

    TaskScheduler.sequenceId++

    return result
  }

  get size(): number {
    return this.#taskPool.size
  }

  setSchedulerChangeListener(listener: SchedulerChangeListener): void {
    this.#schedulerChangeListener = listener
  }

  /**
   添加任务
   * @param task
   */
  addTask(task: T): void {
    task.setTaskScheduler(this)
    this.#taskPool.add(task)
    this.#notifyTaskAdd(task)
    this.#startTask(task)

    if (this.#isFileSizeExceedLimit(task.toJson().fileSize)) {
      this.#notifyExceedMaximumFileSize()
    }
  }

  resumeAllPausedTasks(): void {
    for (const task of this.#taskPool.getAllPausedTasks()) {
      this.resumeTask(task)
    }
  }

  pauseAllTask(): void {
    for (const task of this.#taskPool.getAllGoingAndWaitingTasks()) {
      this.pauseTask(task, false)
    }
  }

  removeAllTask(): void {
    for (const task of this.#taskPool.getAllTask()) {
      this.removeTask(task)
    }
  }

  restartTask(task: T): void {
    task.canChangeState = true
    this.#startTask(task)
  }

  /**
   * 触发暂停任务,暂停全部任务的时候startNext为false
   * @param task
   */
  pauseTask(task: T, startNext = true): void {
    task.state = TaskState.PAUSE
    task.clearTimer()
    task.pause()
    task.canChangeState = true

    startNext && this.#startNext()
    this.#notifyTaskChange(task)
  }

  /**
   * 任务从暂停唤醒
   * @param task
   */
  resumeTask(task: T): void {
    if (task.toJson().transmissionTaskType === TransmissionTaskType.ZIP) return

    if (this.#canExecuteImmediately()) {
      task.state = TaskState.GOING
      task.resume()
    } else {
      task.state = TaskState.WAITING
    }
    task.canChangeState = true
    this.#notifyTaskChange(task)
  }

  /**
   * 任务执行失败
   * @param task
   */
  setTaskError(task: T): void {
    task.state = TaskState.FAILED
    task.clearTimer()
    task.canChangeState = true
    this.#notifyTaskChange(task)
    this.#startNext()
    // task.track()
  }

  /**
   * 任务执行完成且成功
   * @param task
   */
  setTaskSucceed(task: T): void {
    task.state = TaskState.SUCCESS
    task.clearTimer()
    task.canChangeState = true
    this.#notifyTaskChange(task)
    this.#schedulerChangeListener?.onSuccess(task)
    this.#startNext()
    // task.track()
  }

  async removeTask(task: T, needContinue = true, needCallService = true): Promise<void> {
    if (task.state !== TaskState.SUCCESS && task.state !== TaskState.FAILED) {
      const result = await task.onRemove(needCallService)

      if (!result) return
    }

    this.#taskPool.remove(task)
    this.#schedulerChangeListener?.onUpdated(task, needCallService)
    this.#notifyTaskChange(task)

    if (this.#canExecuteImmediately() && needContinue) this.#startNext()
  }

  /**
   * 接受task任务状态变化
   * @param task
   */
  updateTask(task: T, isFreezed?: boolean): void {
    this.#extendedTaskTime(isFreezed)
    this.#notifyTaskChange(task)
  }

  getAllTask(): T[] {
    return this.#taskPool.getAllTask()
  }

  includesTaskByState(states: TaskState[]): boolean {
    return this.#taskPool.includesTaskByState(states)
  }

  getTaskByNoticeId(id: string): T | undefined {
    return this.#taskPool.getAllTask().find((task) => task.noticeId === id)
  }

  #startTask(task: T, isFromQueue = false): void {
    if (this.#canExecuteImmediately()) {
      task.state = TaskState.GOING
      const timeout = isFromQueue
        ? TASK_FROM_QUEUE_START_DELAY
        : TASK_START_DELAY * this.#taskPool.getOnGoingTaskCount()

      setTimeout(() => task.start(), timeout)
    } else {
      task.state = TaskState.WAITING
    }

    this.#notifyTaskChange(task)
  }

  #startNext(): void {
    const nextTask = this.#taskPool.getNextTask()

    if (nextTask) {
      this.#startTask(nextTask, true)
    }
  }

  /**
   * 通知更新视图
   * @param task
   */
  #notifyTaskChange(task: T): void {
    this.#schedulerChangeListener?.onUpdated(task)
  }

  #notifyTaskAdd(task: T): void {
    this.#schedulerChangeListener?.onAdded(task)
  }

  #canExecuteImmediately(): boolean {
    return this.#taskPool.getOnGoingTaskCount() < this.#maxParallelNum
  }

  #notifyExceedMaximumFileSize(): void {
    this.#schedulerChangeListener?.onExceedMaximumFileSize()
  }

  /**
   * 监控传输任务是否超过设置的限额
   */
  #isFileSizeExceedLimit(fileSize: number): boolean {
    return fileSize > TRANSMISSION_FILE_SIZE_MAXIMUM_LIMIT
  }

  #resumeFailedTask = debounce(() => {
    for (const task of this.getAllTask()) {
      task.state === TaskState.FAILED && this.resumeTask(task)
    }
  }, 1000)

  #extendedTaskTime = throttle(
    (isFreezed?: boolean) => {
      if (isFreezed) return

      for (const task of this.#taskPool.getOnGoingTasks()) {
        task.setTimer()
      }
    },
    10 * 1000,
    {
      leading: true,
      trailing: false,
    }
  )
}
